import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash, Plus, QrCode, PencilSimple, Check, X } from "@phosphor-icons/react";

export default function CompanyUpiPanel({ compact = false }) {
    const [upis, setUpis] = useState([]);
    const [upi, setUpi] = useState("");
    const [label, setLabel] = useState("");
    const [busy, setBusy] = useState(false);
    // Inline edit state — { id, value }
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/settings/upis").then((r) => setUpis(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const add = async (e) => {
        e.preventDefault();
        if (!upi.trim()) return;
        setBusy(true);
        try {
            await api.post("/settings/upis", { upi_id: upi.trim(), label: label.trim(), active: true });
            toast.success("UPI ID added");
            setUpi("");
            setLabel("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Remove this UPI ID?")) return;
        await api.delete(`/settings/upis/${id}`);
        toast.success("Removed");
        load();
    };

    const startEdit = (u) => setEditing({ id: u.id, value: u.label || "" });
    const cancelEdit = () => setEditing(null);

    const saveEdit = async () => {
        if (!editing) return;
        try {
            await api.patch(`/settings/upis/${editing.id}`, { label: editing.value.trim() });
            toast.success("'Belongs to' updated");
            setEditing(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="company-upi-panel">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
                <div className="flex items-center gap-2">
                    <QrCode size={18} weight="duotone" className="text-blue-600" />
                    <h2 className="font-display text-base font-semibold text-slate-900">Company UPI IDs</h2>
                </div>
                <span className="text-xs text-slate-500">{upis.length} registered</span>
            </div>
            <form onSubmit={add} className="grid grid-cols-1 gap-2 border-b border-slate-100 p-4 sm:grid-cols-12">
                <div className="sm:col-span-5">
                    <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">UPI ID *</Label>
                    <Input
                        data-testid="upi-id-input"
                        placeholder="mdimpex@ybl"
                        required
                        value={upi}
                        onChange={(e) => setUpi(e.target.value)}
                        className="mt-1"
                    />
                </div>
                <div className="sm:col-span-5">
                    <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Belongs to</Label>
                    <Input
                        data-testid="upi-label-input"
                        placeholder="e.g. Goodluck, Kundra Agencies…"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        className="mt-1"
                    />
                </div>
                <div className="flex items-end sm:col-span-2">
                    <Button
                        type="submit"
                        data-testid="add-upi-btn"
                        disabled={busy || !upi.trim()}
                        className="h-10 w-full bg-blue-600 text-white hover:bg-blue-700"
                    >
                        <Plus size={16} className="mr-1" /> Add
                    </Button>
                </div>
            </form>
            {upis.length === 0 ? (
                <div className="px-5 py-6 text-center text-sm text-slate-500">
                    No UPI IDs registered yet. Add at least one so slips can be verified.
                </div>
            ) : (
                <ul className="divide-y divide-slate-100" data-testid="upi-list">
                    {upis.map((u) => {
                        const isAutoAdded = (u.label || "").toLowerCase().includes("auto-added");
                        const isEditingThis = editing && editing.id === u.id;
                        return (
                            <li key={u.id} data-testid={`upi-row-${u.id}`} className={`flex items-center justify-between gap-3 px-5 py-3 ${isAutoAdded && !isEditingThis ? "bg-amber-50/40" : ""}`}>
                                <div className="min-w-0 flex-1">
                                    <div className="font-mono text-sm font-semibold text-slate-900">{u.upi_id}</div>
                                    {isEditingThis ? (
                                        <div className="mt-1.5 flex items-center gap-2">
                                            <Input
                                                autoFocus
                                                data-testid={`upi-label-edit-${u.id}`}
                                                value={editing.value}
                                                onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") saveEdit();
                                                    if (e.key === "Escape") cancelEdit();
                                                }}
                                                placeholder="Belongs to (business / owner)"
                                                className="h-8 text-xs"
                                            />
                                            <button
                                                type="button"
                                                data-testid={`upi-save-${u.id}`}
                                                onClick={saveEdit}
                                                className="rounded-md bg-emerald-600 px-2 py-1 text-white hover:bg-emerald-700"
                                                title="Save"
                                            ><Check size={13} weight="bold" /></button>
                                            <button
                                                type="button"
                                                onClick={cancelEdit}
                                                className="rounded-md border border-slate-200 px-2 py-1 text-slate-600 hover:bg-slate-100"
                                                title="Cancel"
                                            ><X size={13} weight="bold" /></button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            data-testid={`upi-edit-${u.id}`}
                                            onClick={() => startEdit(u)}
                                            className="mt-0.5 inline-flex items-center gap-1 rounded px-1 py-0.5 text-left text-xs text-slate-500 hover:bg-blue-50 hover:text-blue-700"
                                            title="Rename this UPI's 'Belongs to' label"
                                        >
                                            <span className={isAutoAdded ? "italic text-amber-700" : ""}>
                                                {u.label || "Set 'Belongs to'…"}
                                            </span>
                                            <PencilSimple size={11} weight="bold" className="opacity-60" />
                                        </button>
                                    )}
                                </div>
                                <div className="flex items-center gap-1">
                                    {!isEditingThis && (
                                        <button
                                            type="button"
                                            data-testid={`upi-edit-btn-${u.id}`}
                                            onClick={() => startEdit(u)}
                                            className="rounded-md border border-blue-300 bg-blue-50 px-2 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-100"
                                            title="Edit the 'Belongs to' label"
                                        >
                                            <PencilSimple size={13} weight="fill" className="inline" /> <span className="hidden sm:inline">Edit</span>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        data-testid={`remove-upi-${u.id}`}
                                        onClick={() => remove(u.id)}
                                        className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
                                        title="Remove UPI"
                                    >
                                        <Trash size={16} />
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
